import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { SearchOutlined, ClockCircleOutlined, EyeOutlined, ArrowRightOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import "./Blog.css";

const BlogList = () => {
  const [blogs, setBlogs]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const navigate = useNavigate();

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/Blog`);
      const raw = res.data?.data;
      const list = Array.isArray(raw) ? raw : (raw?.data ?? res.data ?? []);
      setBlogs(list);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { fetchBlogs(); }, []);

  const categories = ["all", ...Array.from(
    new Set(blogs.map((b) => b.category).filter(Boolean))
  )];

  const filtered = blogs.filter((b) => {
    const matchSearch = !search ||
      b.title?.toLowerCase().includes(search.toLowerCase()) ||
      b.excerpt?.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === "all" || b.category === activeCategory;
    return matchSearch && matchCat;
  });

  const [featured, ...rest] = filtered;

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

  const stripHtml = (html) =>
    html ? html.replace(/<[^>]*>/g, "").slice(0, 140) + "…" : "";

  return (
    <>
      <HeaderLayout />

      {/* Hero */}
      <div className="bl-hero">
        <div className="bl-hero-inner">
          <div className="bl-hero-eyebrow">From the Blog</div>
          <h1 className="bl-hero-title">
            Insights, guides &<br />
            <span className="bl-hero-accent">how-to articles</span>
          </h1>
          <p className="bl-hero-sub">
            Tips on PDF conversion, QR codes, productivity tools, and more — written for everyone.
          </p>
          <div className="bl-search-wrap">
            <SearchOutlined className="bl-search-icon" />
            <input
              className="bl-search-input"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bl-page">

        {/* Category pills */}
        {categories.length > 1 && (
          <div className="bl-categories">
            {categories.map((c) => (
              <button
                key={c}
                className={`bl-cat-pill ${activeCategory === c ? "active" : ""}`}
                onClick={() => setActiveCategory(c)}
              >
                {c === "all" ? "All Posts" : c.charAt(0).toUpperCase() + c.slice(1)}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="bl-loading"><Spin size="large" /></div>
        ) : filtered.length === 0 ? (
          <div className="bl-empty">
            <div className="bl-empty-icon">📄</div>
            <div className="bl-empty-title">No articles found</div>
            <div className="bl-empty-sub">Try a different search or category</div>
          </div>
        ) : (
          <>
            {/* Featured post — only on unfiltered view */}
            {featured && !search && activeCategory === "all" && (
              <div className="bl-featured" onClick={() => navigate(`/blog/${featured.slug}`)}>
                <div className="bl-featured-img-wrap">
                  {featured.coverImageUrl
                    ? <img src={featured.coverImageUrl} alt={featured.title} className="bl-featured-img" />
                    : <div className="bl-featured-img-placeholder" />}
                  {featured.category && (
                    <span className="bl-featured-cat">{featured.category}</span>
                  )}
                </div>
                <div className="bl-featured-body">
                  <div className="bl-featured-label">Featured Article</div>
                  <h2 className="bl-featured-title">{featured.title}</h2>
                  <p className="bl-featured-excerpt">
                    {featured.excerpt || stripHtml(featured.content)}
                  </p>
                  <div className="bl-featured-meta">
                    {featured.readingTimeMinutes > 0 && (
                      <span><ClockCircleOutlined /> {featured.readingTimeMinutes} min read</span>
                    )}
                    {featured.viewCount > 0 && (
                      <span><EyeOutlined /> {featured.viewCount.toLocaleString()}</span>
                    )}
                    <span>{formatDate(featured.publishedAt || featured.createdAt)}</span>
                  </div>
                  <button className="bl-featured-cta">
                    Read Article <ArrowRightOutlined />
                  </button>
                </div>
              </div>
            )}

            {/* Grid */}
            {(search || activeCategory !== "all" ? filtered : rest).length > 0 && (
              <div className="bl-grid-section">
                {!search && activeCategory === "all" && (
                  <div className="bl-section-label">More Articles</div>
                )}
                <div className="bl-grid">
                  {(search || activeCategory !== "all" ? filtered : rest).map((blog) => (
                    <div
                      key={blog.blogId ?? blog.slug}
                      className="bl-card"
                      onClick={() => navigate(`/blog/${blog.slug}`)}
                    >
                      <div className="bl-card-img-wrap">
                        {blog.coverImageUrl
                          ? <img src={blog.coverImageUrl} alt={blog.title} className="bl-card-img" />
                          : <div className="bl-card-img-placeholder" />}
                        {blog.category && (
                          <span className="bl-card-cat">{blog.category}</span>
                        )}
                      </div>
                      <div className="bl-card-body">
                        <h3 className="bl-card-title">{blog.title}</h3>
                        <p className="bl-card-excerpt">
                          {blog.excerpt || stripHtml(blog.content)}
                        </p>
                        <div className="bl-card-footer">
                          <div className="bl-card-meta">
                            {blog.readingTimeMinutes > 0 && (
                              <span><ClockCircleOutlined /> {blog.readingTimeMinutes} min</span>
                            )}
                            <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
                          </div>
                          <span className="bl-card-read-link">Read <ArrowRightOutlined /></span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <FooterLayout />
    </>
  );
};

export default BlogList;
